import { atom, atomFamily, DefaultValue, selector } from "recoil"
import { GRID_PAGE_NAME, Routes } from "../../../constants"
import { convertDateStringToISO } from "../../../util"
import {
  ShowDetail,
  ShowGridBranding,
  ShowRoom,
  ShowsErrorMessageType
} from "./types"

/** States  */

// To store shows/rooms
const meetingRooms = atomFamily({
  key: "meetingRooms",
  default: null
})

const meetingRoomIds = atom<string[]>({
  key: "meetingRoomIds",
  default: []
})

export const navigatedFromShowListAtom = atom<boolean>({
  key: "navigatedFromShowListAtom",
  default: GRID_PAGE_NAME === Routes.SHOWS.replace("/", "")
})

export const showGridBrandingAtom = atom<ShowGridBranding | null>({
  key: "showGridBrandingAtom",
  default: null
})

export const isShowLoadingAtom = atom<boolean>({
  key: "isShowLoadingAtom",
  default: true
})

export const loadingRoomHashAtom = atom<string | null>({
  key: "loadingRoomHashAtom",
  default: null
})

export const showsErrorMessageAtom = atom<ShowsErrorMessageType | null>({
  key: "showsErrorMessageAtom",
  default: null
})

/** handler */

const getRoomId = (room: ShowRoom) => {
  return room?.moderatorHash ?? room?.broadcasterHash ?? room?.audienceHash
}

// Get the current date
const currentDate = new Date().getTime()

/**
 * Sort logic
 *  - Live show should be come first
 *  - Then the room should be sort according to countdown time
 *  - undefined and old date ( less then current date) should be at end
 */
const sortShowList = (room1: ShowRoom, room2: ShowRoom) => {
  if (room1.isLive && !room2.isLive) {
    return -1 // room1 comes before
  } else if (!room1.isLive && room2.isLive) {
    return 1 // room2 comes before
  } else {
    // if countdownTime of is undefined move back
    if (!room1.localCountdownTime) return 1
    if (!room2.localCountdownTime) return -1

    const room1Date = new Date(room1.localCountdownTime).getTime()
    const room2Date = new Date(room2.localCountdownTime).getTime()

    if (room1Date < currentDate && room2Date < currentDate) {
      // Both dates are in the past, sort by date in ascending order
      return room1Date - room2Date
    } else if (room1Date < currentDate) {
      // Date 1 is in the past, it should come later
      return 1 // room2 comes before room1
    } else if (room2Date < currentDate) {
      // Date 2 is in the past, it should come later
      return -1 // room1 comes before room2
    } else {
      // Both dates are in the future, sort by date in ascending order
      return room1Date - room2Date
    }
  }
}

export const showListSelector = selector<ShowRoom[]>({
  key: "showListSelector",
  get({ get }) {
    const roomIds = get(meetingRoomIds)

    return roomIds
      .map(id => {
        const room = get(meetingRooms(id))
        return {
          ...room,
          // add isLive property by checking camEnabled and minEnabled count
          isLive: room.camEnabled > 0 || room.micEnabled > 0,
          localCountdownTime: room.countdownTimeUTC
            ? convertDateStringToISO(room.countdownTimeUTC)
            : null
        }
      })
      .sort(sortShowList)
  },
  set({ set, reset }, rooms) {
    if (rooms instanceof DefaultValue) return

    // To store new room ids
    let newRoomIds = []
    let removeIds = []

    // traverse the list and add int recoil state
    rooms?.forEach(room => {
      const roomId = getRoomId(room)

      // if thumbnailUrl is null, then remove that room from the list
      if (!room.thumbnailUrl || room.isExpired) {
        reset(meetingRooms(roomId))
        removeIds.push(roomId)
        return
      }

      set(meetingRooms(roomId), (prevRoom = {}) => {
        // if prev room detail is not present, the room is new
        if (!prevRoom) {
          newRoomIds.push(roomId)
        }

        // append the data
        return {
          id: roomId,
          ...prevRoom,
          ...room
        }
      })
    })

    // add new ids and remove delete room ids
    set(meetingRoomIds, prevIds =>
      Array.from(new Set([...prevIds, ...newRoomIds])).filter(
        id => !removeIds.includes(id)
      )
    )
  }
})

// accept show api response and send show details to UI component
export const showsSelector = selector<ShowDetail>({
  key: "showsSelector",
  get: ({ get }) => {
    return {
      ...get(showGridBrandingAtom),
      rooms: get(showListSelector)
    }
  },

  // Handle api response payload
  set: ({ get, set }, payload) => {
    if (payload instanceof DefaultValue) return

    set(isShowLoadingAtom, false)
    set(showListSelector, payload.rooms)

    // Update the value or set prev value if null
    set(showGridBrandingAtom, prev => ({
      gridBackgroundImage:
        payload.gridBackgroundImage ?? prev?.gridBackgroundImage,
      gridBackgroundColor:
        payload.gridBackgroundColor ?? prev?.gridBackgroundColor,
      gridTitleColor: payload.gridTitleColor ?? prev?.gridTitleColor
    }))
  }
})
